import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { LanguageDto } from "@/api/types";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { Notice } from "@/design-system/Notice";
import { fallbackLanguageCode, hardcodedTranslations } from "@/helpers/hardcoded-translations";
import { FALLBACK_UI_LANGUAGE } from "@/helpers/languages";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { StartupLoading } from "@/providers/StartupLoading";
import { useGeneralQueries } from "@/queries/general/queryOptions";

import { I18nProvider } from "./implementation/I18nProvider";
import type { Translations } from "./implementation/types";

const disableTranslations = getLocalStorageValue<boolean>("disableTranslations", false);
const browserLanguage = window.navigator.language.split("-")[0];

/** Loads a dictionary for the local preference or root user's language. Never clears application data or changes a parent context. */
export function TranslationsProvider({
  children,
  preferredLanguage,
}: {
  children: React.ReactNode;
  preferredLanguage?: LanguageDto["id"];
}): React.ReactNode {
  const queryClient = useQueryClient();
  const queries = useGeneralQueries();
  const { data: languages, error: languagesError } = useQuery(queries.languages({ subset: "ui" }));
  // Keep the saved preference until the supported list arrives. Do not overwrite it with
  // English while an uncached supported-language request is still pending.
  const [localPreference, setLocalPreference] = useState(() =>
    getLocalStorageValue<string>(LOCAL_STORAGE_KEYS.USER_SELECTED_LANGUAGE, ""),
  );
  const language =
    [preferredLanguage, localPreference, browserLanguage, FALLBACK_UI_LANGUAGE].find((candidate) =>
      languages?.some((supported) => supported.id === candidate),
    ) ?? FALLBACK_UI_LANGUAGE;
  const {
    data: translations,
    error: translationsError,
    refetch: retryTranslations,
  } = useQuery({
    ...queries.translations(language, disableTranslations),
    staleTime: Infinity,
    enabled: languages !== undefined,
    retry: 3,
    retryDelay: (attempt) => attempt * 1000,
  });

  // Retain the last complete language/dictionary pair while the next dictionary loads.
  // This is a render-time snapshot, not a second query cache or child-to-parent update.
  const [displayed, setDisplayed] = useState<{ language: string; translations: Translations }>();
  const displayedLanguage = displayed?.language;
  const displayedTranslations = displayed?.translations;
  if (translations && (displayedLanguage !== language || displayedTranslations !== translations)) {
    setDisplayed({ language, translations: translations as Translations });
  }
  useEffect(() => {
    if (displayedLanguage) {
      updateLocalStorage(LOCAL_STORAGE_KEYS.USER_SELECTED_LANGUAGE, displayedLanguage);
      document.documentElement.lang = displayedLanguage;
    }
  }, [displayedLanguage]);

  if (import.meta.env.DEV && import.meta.hot) {
    import.meta.hot.accept("./english.json", async () => {
      await queryClient.invalidateQueries({ queryKey: queries.translations(language, disableTranslations).queryKey });
    });
  }

  if (!displayed) {
    return languagesError || translationsError ? <ConnectionFailurePage poEditorCode={language} /> : <StartupLoading />;
  }

  const errorCopy = (
    hardcodedTranslations[displayed.language as keyof typeof hardcodedTranslations] ??
    hardcodedTranslations[fallbackLanguageCode]
  ).errors.connectionFailure;

  return (
    <I18nProvider
      language={displayed.language}
      translations={displayed.translations}
      additionalOptions={{ transKeepBasicHtmlNodesFor: ["i", "b"] }}
      onChangeLanguage={setLocalPreference}
      onMissingKey={(key, languageId) => {
        if (!disableTranslations) {
          Sentry.captureException(`Missing translation for ${languageId}: ${key}`);
        }
      }}
      onMissingVariable={(key, value, languageId) => {
        if (!disableTranslations) {
          Sentry.captureException(`Missing variable in ${value} in translation for ${languageId}: ${key}`);
        }
      }}
    >
      {children}
      {translationsError && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-space-md" role="alert">
          <Notice
            variant="danger"
            message={errorCopy.title}
            action={{
              label: errorCopy.refresh,
              onClick: () => {
                void retryTranslations();
              },
            }}
          />
        </div>
      )}
    </I18nProvider>
  );
}
